/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { BankAccount, BankAnalysis, BankItem, BankRule } from '@src/app/model/bankAccount';
import { forkJoin, of } from 'rxjs';
import { BCode } from '@src/app/model/bcode';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { maxChars, minChars, required } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { LinkBankAccountAction } from '@src/app/modules/txn/bank-account-page/link-bank-account-action';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GmtAgoPipe } from '@src/app/shared/pipes/gmt-ago.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BankOption, GoCardlessAccess, GoCardlessOpenBankingService } from '@src/app/modules/txn/bank-account-page/gocardless-open-banking-service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { Field } from '@src/app/shared/field/Field';
import { NgTemplateOutlet, Location } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { BankTransferAction } from '@src/app/modules/txn/bank-account-page/bank-transfer-action';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { BankChargesAction } from '@src/app/modules/txn/bank-account-page/bank-charges-action';
import { ManualReconciliationAction } from '@src/app/modules/txn/bank-account-page/manual-reconciliation/manual-reconciliation-action';
import { Txn } from '@src/app/model/txn';
import { ErrorCode } from '@src/app/shared/form/form-error/form-error.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { first } from 'rxjs/operators';
import { MyInjector } from '@src/app/app.module';
import { BCodePageComponent } from '@src/app/modules/budget/b-code-page/b-code-page.component';
import { MatDialog } from '@angular/material/dialog';
import { BankInterestAction } from '@src/app/modules/txn/bank-account-page/bank-interest-action';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { BankPageNavRoute } from '@src/app/routes/bank-page-nav';
import { BankAccountPageNavRoute } from '@src/app/routes/app-router';
import { BankInPageNavRoute } from '@src/app/routes/bank-in-page-nav';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';
import { Consent, YapilyAccount, YapilyBankOption, YapilyOpenBankingService } from '@src/app/modules/txn/bank-account-page/yapily-open-banking-service';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '@src/app/shared/message.service';
import { FromNowPipe } from '@src/app/shared/pipes/from-now.pipe';
import { uuid } from '@src/app/model/abstract-object';

@Component({
    selector: 'app-bank-account-page',
    standalone: true,
    templateUrl: './bank-account-page.component.html',
    styleUrls: ['./bank-account-page.component.scss'],
    imports: [FormPageComponent, MatButtonModule, MatIconModule, GmtAgoPipe, MatTooltipModule,
        NgTemplateOutlet, MatProgressSpinnerModule, FromNowPipe]
})
export class BankAccountPageComponent extends AbstractPageComponent {
    dataSvc = inject(BankAccountService);
    bCodeSvc = inject(BCodeService);
    private gocobSvc = inject(GoCardlessOpenBankingService);
    private cds = inject(ConfirmDialogService);
    private location = inject(Location);
    private yapilySvc = inject(YapilyOpenBankingService);
    private msgSvc = inject(MessageService);
    private currentUserSvc = inject(CurrentUserService);


    gocAccesses: GoCardlessAccess[] = [];
    yapilyConsents: Consent[] = [];

    checkedAccess = 2;


    totalBanks = 0;
    linkedBanks = 0;

    unLinked = $localize`This account does not match the IBAN of any bank account you have setup.
    You should setup a bank account to match this IBAN, or update existing accounts to match,
    or revoke the access as it cannot currently be used.
    `

    analysisGrid = new GridField({
        field:
        {
            label: 'Cashflow', value: 'txnAnalysis', sendServer: false,
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: this.balanceGridRow.bind(this)
    });

    statementGrid = new GridField({
        field:
        {
            label: 'Reconciliations', value: 'statements', sendServer: false,
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: this.statementGridRow.bind(this)
    });

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc);

    rulesGrid = new GridField({
        field:
        {
            label: 'Rules', value: 'rules', sendServer:false,
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: () => [
            FieldMaker.id(),
            FieldMaker.rev(),
            FormTextComponent.make('Field', 'field', { readonly: true }),
            FormTextComponent.make('Matches', 'matches', { readonly: true }),
            FormTextComponent.make('Applied to', 'appliedTo', { readonly: true,
                calculateValue: (o: BankRule) => o.unitId ? o.unitName : o.supplierName}),
            FieldMaker.notes(),
            FormCheckboxComponent.make('Active', 'isActive'),
            FormNumberComponent.make('Used', 'used'),
            FieldMaker.spacer()
        ]
    });

    bCodeValidator = (o: AppFormControl) => {
        const msg = $localize `You should create a new budget code for each bank account`;
        const defBCs = this.currentUserSvc.currentTeam.defaultBCodes;
        const dbc = defBCs.find(dbc => dbc.bCodeId === o.value);
        if (dbc) {
            return new ErrorCode('Code Not Allowed', msg);
        }
        const used = this.bankAccounts.filter(ba => ba.bCodeId === o.value);
        if (used.length > 0) {
            if (used.length > 1) {
                return new ErrorCode('Already Used', msg);
            }
            if (used[0].id !== this.page.form.focusItem.id) {
                return new ErrorCode('Already Used', msg);
            }
        }

        return null
    }

    assetPickListField = FormPicklistComponent.make('Asset Budget Code', 'bCodeId', 'bCode',
        { service: this.bCodeSvc, serviceFilter: new HttpParams().set('typeId', BCode.TYPE.ASSET.id) },
        { formColumn: 4, validators: [required, this.bCodeValidator.bind(this)] }
    );

    config = new FormConfig({
        navRoute: BankAccountPageNavRoute,
        docPath: 'omc-books/banking/bank-accounts',
        title: $localize`Bank`,
        help: $localize`Bank accounts`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormTextComponent.make('Name', 'name', { validators: [required, maxChars(20)] }),
                FormTextComponent.make('IBAN', 'IBAN', { formColumn: 1, validators: [minChars(16), maxChars(30)] }),
                FormTextComponent.make('BIC', 'BIC', { formColumn: 1, validators: [maxChars(11), minChars(8)] }),

                FormDateComponent.make('Last Reconciliation Date', 'lastRecDate', {disable: true, formColumn: 2}),
                FormNumberComponent.makeCurrency('Last Reconciled Balance', 'lastRecBalance', {disable: true, formColumn: 2}),

                FormCheckboxComponent.make('Linked', 'linkedGoc', {
                    calculateValue: (o: BankAccount) => o.gocId,
                    hint: 'Has this account been linked so data can be reconciled',
                    formColumn: 3,
                    disable: true
                }),
                FormDateTimeComponent.make('Balance as at', 'gocLastPull', { disable: true, formColumn: 3 }),
                FormNumberComponent.makeCurrency('Last Balance', 'lastBalance', { disable: true, formColumn: 3 }),

                FormCheckboxComponent.make('default', 'defaultAcct', {
                    hint: 'Is this the default account should be selected when entering payments',
                    formColumn: 5
                }),
                this.assetPickListField,
                FormPicklistComponent.make('Charges Budget Code', 'chargesBCodeId', null,
                    { service: this.bCodeSvc,
                        serviceFilter: new HttpParams().set('typeId', BCode.TYPE.EXPENSE.id),
                    },
                    { formColumn: 4, validators: [required] }),

                FormPicklistComponent.make('Interest Income Budget Code', 'incomeBCodeId', null,
                    {
                        service: this.bCodeSvc,
                        serviceFilter: new HttpParams().set('typeId', BCode.TYPE.INCOME.id),
                    },
                    { formColumn: 4, validators: [required] }),


                // Incomplete: FormCheckboxComponent.make('Stripe Account', 'defaultPayz', {formColumn: 4}),
                FieldMaker.notes({ formColumn: 5 }),
                this.analysisGrid,
                this.statementGrid,
                this.rulesGrid
            ],
            formLayout: [
                { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
                { cells: [{ colspan: 5, pageTab: 'yes' }] },
            ]
        }),
        service: this.dataSvc,
        mode: 'list',
        actions: [ new BankChargesAction(), new BankTransferAction(), new BankInterestAction(),
            new ManualReconciliationAction() ],
        objectFactory: this.newBankAccount.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        beforeList: this.beforeList.bind(this),
        tabFields: [this.attachmentGrid],
        configReady: this.configReady,
        newOptions: [
            { name: $localize`New ${Txn.TYPE.BANK_IN.name}`, basePath: BankInPageNavRoute.newUrl, params: {}, id:'newReceipt' },
            { name: $localize`New ${Txn.TYPE.BANK_OUT.name}`, basePath: BankOutPageNavRoute.newUrl, params: {}, id: 'newBankOut' },
            { name: $localize`New Bank Account`, basePath: BankAccountPageNavRoute.newUrl, params: {}, id: 'newBankAccount' },
        ],
    });
    bankAccounts: BankAccount[] = [];
    constructor() {
        const activatedRoute = inject(ActivatedRoute);

        super();
        const currentUserSvc = this.currentUserSvc;


        this.subscriptions.push(activatedRoute.queryParams.subscribe( this.processConsents.bind(this) ));

        forkJoin({
            bankAccounts: this.dataSvc.get<BankAccount>(),
            dbcs: currentUserSvc.getDefaultBCodes().pipe(first()),
        }).subscribe(result => {
            this.bankAccounts = result.bankAccounts;
            this.configReady.next(null);
            return true;
        });



        //this.getSampleData().subscribe ( d => this.gocAccesses = d as unknown as GoCardlessAccess[]);
    }

    processConsents(params) {
        console.log('Process Consent', params);
        if (params.consent && params['application-user-id'] && params['user-uuid'] && params.institution) {
            console.warn('This is a yappily consent');
            this.yapilySvc.consentApproved({
                consent: params.consent,
                institution: params.institution,
                userUuid: params['user-uuid']
            }).subscribe( x => {
                if (x) {
                    this.yapilyConsents = x;
                    this.checkedAccess = 2;
                    this.location.replaceState(this.location.path().split('?')[0]);
                }
                this.msgSvc.show($localize `Consent received to access ${params.institution} `);
            }, failure => {
                this.msgSvc.showError(failure, 'Saving Consent');
            })
        }
    }

    statementGridRow(o: BankItem) {
        const openingBal = FormNumberComponent.makeCurrency("Opening Balance", 'data.openingBal', {
            readonly: true, sendServer: false
        });
        if (o && o.data.openingBal < 0.00) {
            openingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right';
        }

        const recFromField = FormDateComponent.make('From Date', 'data.fromDate', {
            readonly: true, sendServer: false
        });

        const closingBal = FormNumberComponent.makeCurrency("Closing Balance", 'data.closingBal', {
            readonly: true, sendServer: false
        });
        if (o && o.data.closingBal < 0) {
            closingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right;';
        }

        const nettValue = FormNumberComponent.makeCurrency("Transaction Value", 'txnValue', {
            calculateValue: (o: BankItem) => o.data.closingBal - o.data.openingBal,
            readonly: true
        });
        if (o && ((o.data.closingBal - o.data.openingBal) < 0)) {
            nettValue.cellOpts.style = 'color:red; white-space:nowrap; text-align: right;';
        }
        const recDateField = FormDateComponent.make('Reconciliation Date', 'data.toDate', {
            readonly: true, sendServer: false
        });
        if (o && o.data.toDate < o.data.fromDate) {
            recFromField.cellOpts.style = 'color:red';
            recDateField.cellOpts.style = 'color:red';
        }

        return [
            recFromField,
            openingBal,
            nettValue,
            recDateField,
            closingBal,
            FormNavLinkComponent.make('Items',
                BankPageNavRoute, (o: BankItem) => ({
                    _sort: 'txnDate', _forceTeamId: o.teamId,
                    bankAccountId: o.bankId,
                    txnDate: `><${o?.data.fromDate}:${o?.data.toDate}`,
                }),
                { calculateValue: () => 'Transactions' }
            ),
            FieldMaker.spacer()
        ]
    }

    balanceGridRow(o: BankAnalysis) {
        const openingBal = FormNumberComponent.makeCurrency("Opening Balance", 'openBalance', { readonly: true });
        if (o && o.openBalance < 0.00) {
            openingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right';
        }

        const closingBal = FormNumberComponent.makeCurrency("Balance", 'balance', { readonly: true });
        if (o && o.balance < 0) {
            closingBal.cellOpts.style = 'color:red; white-space:nowrap; text-align: right;';
        }

        return [
            FormTextComponent.make('Period', 'periodName', { readonly: true }),
            FormDateComponent.make('Start Date', 'startDate', { readonly: true }),
            openingBal,
            FormNumberComponent.makeCurrency("Money In", 'debits', { readonly: true }),
            FormNumberComponent.makeCurrency("Money Out", 'credits', { readonly: true }),
            FormDateComponent.make('End Date', 'endDate', { readonly: true }),
            closingBal,
            FormNavLinkComponent.make('Items',
                BankPageNavRoute, (o: BankAnalysis) => ({
                    txnPeriodId: o.txnPeriodId, _sort: 'txnDate', _forceTeamId: o.teamId, bankAccountId: o.bankAccountId
                }),
                { value: 'txns' }
            ),
            FieldMaker.spacer()
        ]
    }

    beforeEdit(ba: BankAccount) {
        let runningBalance = 0;
        ba.txnAnalysis.slice().reverse().forEach(bai => {
            bai.openBalance = runningBalance;
            bai.debits = +bai.debits;
            bai.credits = +bai.credits;
            runningBalance = runningBalance + bai.debits - bai.credits;
            bai.balance = runningBalance;
        })
        ba.attachments = ba.attachments.sort((a, b) => b.createdAt - a.createdAt);

        return ba;
    }

    beforeList(bas: BankAccount[]) {
        this.linkedBanks = 0;
        this.totalBanks = 0;
        bas.forEach(ba => {
            this.totalBanks += 1;
            if (ba.gocId || ba.yapilyAccountId) {
                this.linkedBanks += 1;
            }
        })
        return bas;
    }

    newBankAccount() {
        // agentTeamId is stamped server side from the team's primary agent - see OurModel.
        const ba = new BankAccount();
        this.cds.open($localize`New Budget Code Required`,
            $localize`Remember - To setup a new bank account,
            you should first create a new budget code to hold the asset value.
            Do you want to create a new budget code now?`, () => {
                const dialog = MyInjector.instance.get(MatDialog);
                const data = { data: { typeId: BCode.TYPE.ASSET.id } };
                dialog.open(BCodePageComponent, data).afterClosed().subscribe( (bc: BCode) => {
                    if (bc && bc.typeId === BCode.TYPE.ASSET.id) {
                        this.assetPickListField.picklist.items.push(bc);
                        this.assetPickListField.control.setValue(bc.id);
                    }
                });
            }
        )
        return of(ba);
    }

    linkBank() {
        const linker = new LinkBankAccountAction();
        linker.setup({} as BankAccount);
        linker.action();
    }

    checkAccesses() {
        this.checkedAccess = 0;
        this.gocobSvc.getAccesses().subscribe(response => {
            this.gocAccesses = response;
            this.checkedAccess += 2; // 1 with yap turned on
        }, failure => {
            console.error(failure);
            this.cds.alert('Error', "An error occured checking bank access");
        });
        /*
        this.yapilySvc.getConsents().subscribe( x=> {
            this.yapilyConsents = x;
            this.checkedAccess += 1;
        })
        */ 
    }

    testYapAccess(yap: unknown) {
        console.warn(yap);
    }

    refreshYapAccess(yap: Consent) {

        const title = $localize `Link To Bank`
        const msg = $localize `Connect to ${yap.institution} to refresh access?`
        const doIt = () => {
            this.yapilySvc.requestAccess({ id: yap.institutionId, name: yap.institution } as YapilyBankOption).subscribe( yapilyAccess => {
                window.location.href = yapilyAccess.authorisationUrl;
            }, failure => {
                console.warn('Failed With', failure)
                this.cds.alert('Error', 'Error linking bank account');
            });
        }
        this.cds.open(title, msg, doIt);
    }

    removeYapAccess(yap: Consent) {
        const title = $localize`Remove Access`;
        const accts = this.bankAccounts.filter(acct => acct.yapilyConsentId === yap.consentId);
        const acctNames = accts.map(acct => acct.name.trim()).join(", ");
        const msg2 = accts.length > 0 ?
            $localize`The following accounts ${accts.length} will no longer have transactions collected: ${acctNames}`
            : 'This will not effect any accounts as no accounts are currently linked to this approval';
        const msg = $localize`Do you wish to remove consent? ${msg2}`;
        const doit = () => {
            this.yapilySvc.removeAccess(yap).subscribe(response => {
                if (response) {
                    const idx = this.yapilyConsents.findIndex(o => o.id === yap.id);
                    this.yapilyConsents.splice(idx, 1);
                }
            }, failure => {
                console.error(failure);
                this.cds.alert('Error', 'An error occured removing access');
            })
        }
        this.cds.open(title, msg, doit);
    }

    onYapAccountSelected(bankId: uuid, yapAcct: YapilyAccount, consentId) {
        console.log({bankId, yapAcct, consentId});
        const bank = this.bankAccounts.find(ba => ba.id === bankId);
        const title = $localize `Connect bank ${bank.name}`;
        const msg = $localize  `Connect bank ${bank.name} to ${yapAcct.type} ${yapAcct.IBAN} ${yapAcct.holder}`;
        this.cds.open(title, msg, () => {
            this.yapilySvc.connectBank({bankId, yapilyAccountId: yapAcct.yapilyAccountId, consentId}).subscribe ( ba => {
                if (ba) {
                    yapAcct.ourBankAccountName = ba.name;
                    bank.yapilyAccountId = ba.yapilyAccountId;
                    bank.yapilyConsentId = ba.yapilyConsentId;
                    this.linkedBanks += 1;
                }
            })
        })
    }

    testAccess(goca: GoCardlessAccess) {
        const title = $localize`Test Access`;
        const msg = $localize`Do you want to attempt the bank connection to make sure it works as expected?
        If you continue it may take a few seconds to complete.`
        const doit = () => {
            this.gocobSvc.testAccess(goca).subscribe(response => {
                if (response) {
                    const msg = `${response.accounts} accounts connected with this approval. ${response.description}`
                    this.cds.alert('Success', msg);
                }
            }, failure => {
                console.error(failure);
                const error = failure.error?.message || failure.message;
                this.cds.alert('Error', error);
            })
        }
        this.cds.open(title, msg, doit);
    }

    refreshAccess(goca: GoCardlessAccess) {
        const title = $localize`Refresh Access`;
        const msg = $localize`Do you wish to reapprove Open Banking Access?`
        const doit = () => {
            //{ bankId: bo.id, bankName: bo.name }
            const bo = { name: goca.bankName, id: goca.gocInstitutionId as string } as BankOption;
            this.gocobSvc.requestAccess(bo).subscribe(goca => {
                window.location.href = goca.gocLink;
            }, failure => {
                console.warn('Failed With', failure)
                this.cds.alert('Error', 'Error linking bank account');
            });
        }
        this.cds.open(title, msg, doit);
    }

    removeAccess(goca: GoCardlessAccess) {
        const title = $localize`Remove Access`;
        const accts = goca.accounts.filter(acct => acct['active']);
        const ibans = accts.map(acct => acct.iban.slice(-4)).join(", ");
        const msg2 = accts.length > 0 ?
            $localize`The following accounts ${accts.length} will no longer have transactions collected: ${ibans}`
            : 'This will not effect any accounts as no accounts are currently linked to this approval';
        const msg = $localize`Do you wish to remove Open Banking Access history? ${msg2}`;
        const doit = () => {
            this.gocobSvc.removeAccess(goca).subscribe(response => {
                if (response) {
                    const idx = this.gocAccesses.findIndex(o => o.id === goca.id);
                    this.gocAccesses.splice(idx, 1);
                }
            }, failure => {
                console.error(failure);
                this.cds.alert('Error', 'An error occured removing access');
            })
        }
        this.cds.open(title, msg, doit);
    }
}
